import { useState, useEffect } from 'react';

const COOKIE_KEY = 'faro_energia_cookie_consent';

type ConsentStatus = 'accepted' | 'rejected' | null;

const CookieBanner = () => {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem(COOKIE_KEY);
    if (!stored) {
      const timer = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem(COOKIE_KEY, 'accepted');
    setVisible(false);
  };

  const handleReject = () => {
    localStorage.setItem(COOKIE_KEY, 'rejected');
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-[9999] transition-transform duration-500 ease-out"
      role="dialog"
      aria-label="Aviso de cookies"
      aria-modal="true"
    >
      {/* Backdrop blur strip */}
      <div className="bg-white/95 backdrop-blur-md border-t border-gray-100 shadow-[0_-4px_32px_rgba(26,34,50,0.10)]">
        <div className="max-w-7xl mx-auto px-6 py-5">
          <div className="flex flex-col lg:flex-row lg:items-center gap-4">

            {/* Icon + Text */}
            <div className="flex items-start gap-4 flex-1">
              <div className="w-10 h-10 flex items-center justify-center bg-brand-navy/8 rounded-full shrink-0 mt-0.5">
                <i className="ri-shield-check-line text-brand-navy text-lg"></i>
              </div>
              <div>
                <p className="text-brand-navy font-semibold text-sm mb-1">
                  Uso de cookies en Faro Energía
                </p>
                <p className="text-gray-500 text-sm leading-relaxed">
                  Utilizamos cookies propias y de terceros para mejorar tu experiencia, analizar el tráfico y personalizar el contenido. Puedes aceptar todas las cookies o configurar tus preferencias.{' '}
                  <button
                    onClick={() => setShowDetails(!showDetails)}
                    className="text-brand-teal underline underline-offset-2 hover:text-brand-navy transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {showDetails ? 'Ocultar detalles' : 'Más información'}
                  </button>
                </p>

                {/* Expanded details */}
                {showDetails && (
                  <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {[
                      {
                        icon: 'ri-settings-3-line',
                        title: 'Cookies esenciales',
                        desc: 'Necesarias para el funcionamiento básico del sitio. No pueden desactivarse.',
                        required: true,
                      },
                      {
                        icon: 'ri-bar-chart-2-line',
                        title: 'Cookies analíticas',
                        desc: 'Nos ayudan a entender cómo interactúas con la web para mejorar nuestros servicios.',
                        required: false,
                      },
                      {
                        icon: 'ri-megaphone-line',
                        title: 'Cookies de marketing',
                        desc: 'Usadas para mostrarte publicidad relevante y medir la efectividad de campañas.',
                        required: false,
                      },
                    ].map((item) => (
                      <div
                        key={item.title}
                        className="bg-gray-50 rounded-lg p-3 flex gap-3"
                      >
                        <div className="w-7 h-7 flex items-center justify-center shrink-0">
                          <i className={`${item.icon} text-brand-teal text-base`}></i>
                        </div>
                        <div>
                          <p className="text-brand-navy font-semibold text-xs mb-0.5 flex items-center gap-1.5">
                            {item.title}
                            {item.required && (
                              <span className="text-[10px] bg-brand-teal/15 text-brand-teal px-1.5 py-0.5 rounded-full font-medium">
                                Siempre activas
                              </span>
                            )}
                          </p>
                          <p className="text-gray-500 text-xs leading-relaxed">{item.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <p className="text-gray-400 text-xs mt-2">
                  Consulta nuestra{' '}
                  <a
                    href="#"
                    className="text-brand-teal underline underline-offset-2 hover:text-brand-navy transition-colors"
                  >
                    Política de Cookies
                  </a>{' '}
                  y nuestra{' '}
                  <a
                    href="#"
                    className="text-brand-teal underline underline-offset-2 hover:text-brand-navy transition-colors"
                  >
                    Política de Privacidad
                  </a>
                  .
                </p>
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-row lg:flex-col gap-2 lg:shrink-0 lg:min-w-[180px]">
              <button
                onClick={handleAccept}
                className="flex-1 lg:flex-none bg-brand-navy hover:bg-brand-navy/90 text-white text-sm font-semibold px-6 py-2.5 rounded-lg transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                Aceptar todas
              </button>
              <button
                onClick={handleReject}
                className="flex-1 lg:flex-none border border-gray-200 hover:border-brand-navy text-gray-500 hover:text-brand-navy text-sm font-medium px-6 py-2.5 rounded-lg transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                Solo esenciales
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieBanner;
